import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { cfpBaseUrl } from '../../network/urls';
import { buildTree } from '../../services/Functions';
import {
    clearAdminCompanySelection,
    clearAdminCompanySession,
    markAdminCompanySessionActive,
    writePersistedAdminCompanyCode,
} from '../../utils/adminCompany';
import {
    assertInternetConnection,
    normalizeNetworkError,
} from '../../utils/networkConnectivity';

const initialState = {
    name: "",
    userId: "",
    roleTypeId: "",
    roleTypeName: "",
    phoneNo: "",
    email: "",
    cnic: "",
    code: "",
    roleId: null,
    roleName: "",
    showAllApplicationsFlag: null,
    cscID: "",
    csc : "",
    cfcLevelId: "",
    cfcLevelName: "",
    divisionCode: "",
    circleCode: "",
    companyCode:"",
    selectedAdminCompanyCode: "",
    hierarchy: null,
    lastLoginTime: "",
    userStatus: "",
    currentDateTime: "",
    profilePicture: "",
    permissions: [],
    token: "",
    wmsPrivateToken: '',
    loading: false,
    error: null,
}

export const signIn = createAsyncThunk(
    'auth/signIn',
    async (credentials, { rejectWithValue }) => {
        try {
            await assertInternetConnection();

            const formdata = new FormData();
            formdata.append("userId", credentials.UserName);
            formdata.append("password", credentials.Password);
            formdata.append("isLoggedInFromMobile", '0');
            const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/SignInMFA', {
                method: 'POST',
                body: formdata,
                redirect: 'follow',
            });

            const data = await response.json();
            const statusCode = String(data?.status || '');
            const isValidLoginStatus = statusCode === '200' || statusCode === '206';
            if (!response.ok || !isValidLoginStatus) {
                throw new Error(data.message || 'Sign API issue!');
            }

            return data;
        } catch (error) {
            return rejectWithValue(normalizeNetworkError(error, 'Sign API issue!'));
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        signOut: (state) => {
            clearAdminCompanySelection();
            Object.assign(state, initialState);
        },
        stopLoading: (state) => {
            state.loading = false;
        },
        updateName: (state, action) => {
            state.name = action.payload
        },
        updatePhone: (state, action) => {
            state.phoneNo = action.payload
        },
        updateEmail: (state, action) => {
            state.email = action.payload
        },
        updateCnic: (state, action) => {
            state.cnic = action.payload
        },
        updateProfilePicture: (state, action) => {
            state.profilePicture = action.payload
        },
        setUserData: (state, action) => {
            const payload = action.payload;
            state.name = payload.name;
            state.userId = payload.userId;
            state.roleTypeId = payload.roleTypeId;
            state.roleTypeName = payload.roleTypeName;
            state.phoneNo = payload.phoneNo;
            state.email = payload.email;
            state.cnic = payload.cnic;
            state.code = payload.code;
            state.roleId = payload.roleId;
            state.roleName = payload.roleName;
            state.showAllApplicationsFlag = payload.showAllApplicationsFlag;
            state.cscID = payload.cscID;
            state.csc = payload.csc;
            state.cfcLevelId = payload.cfcLevelId;
            state.cfcLevelName = payload.cfcLevelName;
            state.divisionCode = payload.divisionCode;
            state.circleCode = payload.circleCode;
            state.companyCode = payload.companyCode;
            // Do not wipe login-selected DISCO when auth payload omits it
            if (payload.selectedAdminCompanyCode) {
                state.selectedAdminCompanyCode = String(payload.selectedAdminCompanyCode);
                writePersistedAdminCompanyCode(state.selectedAdminCompanyCode);
                markAdminCompanySessionActive();
            }
            state.hierarchy = payload.hierarchy;
            state.currentDateTime = payload.currentDateTime;
            state.lastLoginTime = payload.lastLoginTime;
            state.userStatus = payload.userStatus;
            state.profilePicture = payload.profilePicture;
            state.permissions = buildTree(payload.permissions);
            state.token = payload.token;
            state.wmsPrivateToken = payload.wmsPrivateToken;
        },
        setSelectedAdminCompanyCode: (state, action) => {
            state.selectedAdminCompanyCode = action.payload ? String(action.payload) : "";
            writePersistedAdminCompanyCode(state.selectedAdminCompanyCode);
            if (state.selectedAdminCompanyCode) {
                markAdminCompanySessionActive();
            } else {
                clearAdminCompanySession();
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(signIn.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(signIn.fulfilled, (state) => {
                state.loading = false;
                // No auto-login here to prevent unintended logins
            })
            .addCase(signIn.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    signOut,
    stopLoading,
    updateName,
    updatePhone,
    updateEmail,
    updateCnic,
    lastLoginTime,
    updateProfilePicture,
    setUserData,
    setSelectedAdminCompanyCode
} = authSlice.actions;

export default authSlice.reducer;
